import { getGroupMemberships } from "../api/groupsApi";
import { JSX, useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../supabase/AuthProvider";
import { useUserProfile } from "../context/UserProfileContext";
import { Feature, useEntitlements } from "../context/EntitlementsContext";
import {
  validateUserRole,
  validateOrganizationAccess,
} from "../services/userService";

const DEFAULT_ALLOWED_ROLES = ["admin"];
const APP_HOME_URL = process.env.REACT_APP_APP_HOME_URL || "https://app.pedagogue.io";
const APP_AUTO_HANDOFF_URL = `${APP_HOME_URL}/auto-handoff`;
const APP_LOGIN_URL = `${APP_HOME_URL}/login`;

/**
 * ProtectedRoute - A unified route protection component that handles:
 * - Authentication validation
 * - Role-based access control
 * - Organization-based access control
 * - Future global-role support
 *
 * Usage examples:
 *
 * // Basic admin-only route with organization validation
 * <ProtectedRoute requireOrganization={true} allowedRoles={["admin"]}>
 *   <YourComponent />
 * </ProtectedRoute>
 *
 * // Route without organization validation (for global admin features)
 * <ProtectedRoute requireOrganization={false} allowedRoles={["admin"]}>
 *   <YourComponent />
 * </ProtectedRoute>
 * // Multiple roles allowed
 * <ProtectedRoute requireOrganization={true} allowedRoles={["admin", "super_admin"]}>
 *   <YourComponent />
 * </ProtectedRoute>
 */
interface ProtectedRouteProps {
  children: JSX.Element;
  requireOrganization?: boolean;
  allowedRoles?: string[];
}

const ProtectedRoute = ({
  children,
  requireOrganization = true,
  allowedRoles = DEFAULT_ALLOWED_ROLES,
}: ProtectedRouteProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const routeParams = useParams<{ organizationId: string; organisationId: string }>();
  const organizationId = routeParams.organizationId ?? routeParams.organisationId;
  const [accessGranted, setAccessGranted] = useState(false);
  const [grantedScope, setGrantedScope] = useState("");
  const { user, loading, sessionValidation } = useAuth();
  const { userProfile, loading: profileLoading, profileResolved } = useUserProfile();
  const { entitlements, loading: entitlementsLoading, error: entitlementsError } = useEntitlements();
  const consoleDisabled = userProfile?.role !== "super_admin" &&
    entitlements?.featureOverrides?.[Feature.ADMIN_CONSOLE] === false;
  const consoleUnresolved = !!user && userProfile?.role !== "super_admin" &&
    !entitlements && !entitlementsError;

  const accessScope = JSON.stringify([user?.id, userProfile?.id, userProfile?.role, userProfile?.status, userProfile?.organisation_id, location.pathname, location.search, sessionValidation, consoleDisabled, entitlementsError]);
  useEffect(() => {
    let cancelled = false;
    setAccessGranted(false);
    const grantAccess = () => { if (!cancelled) { setGrantedScope(accessScope); setAccessGranted(true); } };
    const validateAccess = async () => {
      if (loading || profileLoading || (user && (entitlementsLoading || consoleUnresolved))) {
        return;
      }

      // G2 terminal auth: the backend actively rejected the stored session
      // (revoked / blocked / access_locked) and the AuthProvider cleared it.
      // Auto-handoff cannot recover a rejected account and risks a redirect
      // loop, so go straight to the app-web login surface.
      if (sessionValidation === "terminated") {
        const returnTo = `${window.location.origin}${location.pathname}${location.search}`;
        window.location.href = `${APP_LOGIN_URL}?return_to=${encodeURIComponent(returnTo)}`;
        return;
      }

      // Check if user is authenticated
      if (!user) {
        const returnTo = `${window.location.origin}${location.pathname}${location.search}`;
        // Circuit breaker: if auto-handoff already attempted, fall back to /login
        const params = new URLSearchParams(window.location.search);
        if (params.get('handoff_attempted') === '1') {
          window.location.href = `${APP_LOGIN_URL}?return_to=${encodeURIComponent(returnTo)}`;
        } else {
          window.location.href = `${APP_AUTO_HANDOFF_URL}?return_to=${encodeURIComponent(returnTo)}`;
        }
        return;
      }

      // Wait for profile fetch to complete before making access decisions
      if (!profileResolved) {
        return;
      }

      // Profile fetch completed but no profile found — redirect to login
      if (!userProfile) {
        const returnTo = `${window.location.origin}${location.pathname}${location.search}`;
        window.location.href = `${APP_LOGIN_URL}?return_to=${encodeURIComponent(returnTo)}`;
        return;
      }

      if (consoleDisabled || entitlementsError) {
        setAccessGranted(false);
        navigate("/unauthorized", {
          state: {
            from: location.pathname,
            message: consoleDisabled
              ? "Your organisation's plan does not include the Admin Console."
              : "We could not verify your Admin Console access. Please try again.",
          },
        });
        return;
      }

      // Check if user has an allowed role
  // Admin-equivalent roles: organisation_admin, platform_admin, billing_admin
  // all inherit 'admin' access for backward compatibility during multi-role migration
  const ADMIN_EQUIVALENT_ROLES = ["admin", "organisation_admin", "platform_admin", "billing_admin"];
  const roleAllowed =
    validateUserRole(userProfile.role, allowedRoles) ||
    (userProfile.role === "super_admin" && allowedRoles.includes("admin")) ||
    (ADMIN_EQUIVALENT_ROLES.includes(userProfile.role) && allowedRoles.includes("admin"));

  if (!roleAllowed) {
        console.warn(
          `Access denied: User role "${userProfile.role}" not in allowed roles: [${allowedRoles.join(", ")}]`
        );
        navigate("/unauthorized", {
          state: {
            from: location.pathname,
            message: `Access denied. Required roles: ${allowedRoles.join(", ")}`,
          },
        });
        return;
      }

      // If organization validation is not required, grant access
  if (!requireOrganization) {
    grantAccess();
    return;
  }

  if (userProfile.role === "super_admin") {
    grantAccess();
    return;
  }

      // Check if user has organization access
      if (!ADMIN_EQUIVALENT_ROLES.includes(userProfile.role) && !userProfile?.organisation_id) {
        navigate("/unauthorized", {
          state: {
            from: location.pathname,
            message: "Access denied. No organization assigned to your account.",
          },
        });
        return;
      }

      // Check if the requested organization matches the user's organization
      let analyticsSchools: string[] = [];
      if (organizationId && String(userProfile.organisation_id) !== organizationId && /^\/analytics\/dashboard\/[^/]+$/.test(location.pathname)) {
        try { const groups = await getGroupMemberships(); if (cancelled) return; analyticsSchools = groups.flatMap(group => group.schools.map(school => school.id)); }
        catch { if (cancelled) return; }
      }
      const hasOrganizationAccess = validateOrganizationAccess(userProfile.organisation_id, organizationId, analyticsSchools);

      if (!hasOrganizationAccess) {
        navigate("/unauthorized", {
          state: {
            from: location.pathname,
            message:
              "Access denied. You can only access your own organization.",
          },
        });
        return;
      }

      grantAccess();
    };
    void validateAccess();
    return () => { cancelled = true; };
  }, [
    accessScope,
    user,
    userProfile,
    loading,
    profileLoading,
    profileResolved,
    entitlementsLoading,
    entitlementsError,
    consoleUnresolved,
    consoleDisabled,
    sessionValidation,
    organizationId,
    location.pathname,
    location.search,
    navigate,
    requireOrganization,
    allowedRoles,
  ]);

  // G2: a terminated session must never leave an authenticated shell
  // rendered — the redirect to login is in flight.
  if (sessionValidation === "terminated") {
    return null;
  }

  if (loading || profileLoading || (user && (!profileResolved || entitlementsLoading || consoleUnresolved))) {
    return (
      <div className="min-h-screen w-full flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600 font-medium">{"Loading..."}</p>
        </div>
      </div>
    );
  }

  if (!user || !accessGranted || grantedScope !== accessScope || consoleDisabled || entitlementsError) {
    return null;
  }

  return children;
};

export default ProtectedRoute;
